import { makeApiCall } from "@/store/apiRequest";

let cachedLimits = null;
let fetchPromise = null;

/**
 * Fetch subscription plan limits from API:
 * POST https://apis.roomrounds.com/api/subscriptionPlan/getSubscriptionPlansLimit
 */
export async function fetchSubscriptionPlansLimit(forceRefresh = false) {
  if (cachedLimits && !forceRefresh) {
    return cachedLimits;
  }
  if (fetchPromise && !forceRefresh) {
    return fetchPromise;
  }
  fetchPromise = (async () => {
    try {
      const res = await makeApiCall(
        "subscriptionPlan/getSubscriptionPlansLimit",
        "POST",
        {}
      );
      if (res?.data?.succeeded && Array.isArray(res.data.data)) {
        cachedLimits = res.data.data;
        return cachedLimits;
      }
      return cachedLimits || [];
    } catch (e) {
      console.error("fetchSubscriptionPlansLimit error:", e);
      return cachedLimits || [];
    } finally {
      fetchPromise = null;
    }
  })();
  return fetchPromise;
}

export function getCachedSubscriptionLimits() {
  return cachedLimits;
}

/**
 * Find plan limits item by plan name and billing mode.
 * Returns { maxRooms, maxEmployees, maxTemplates, ... } or null
 */
export function findPlanLimit(planName, limits = cachedLimits, isPrepay = false) {
  const list =
    Array.isArray(limits) && limits.length > 0 ? limits : cachedLimits || [];
  if (!list || list.length === 0) return null;

  const raw = String(planName || "").trim();
  const clean = raw.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  if (!clean) return null;

  // 1. If 3-year prepay requested
  if (isPrepay) {
    const baseClean = clean
      .replace("package3year", "")
      .replace("prepay", "")
      .replace("3year", "");
    const prepayMatch = list.find((item) => {
      const itemKey = String(item.planName || "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toLowerCase();
      return (
        itemKey.includes("3year") &&
        (itemKey.includes(baseClean) || baseClean.includes(itemKey.replace("package3year", "")))
      );
    });
    if (prepayMatch) return prepayMatch;
  }

  // 2. Exact alphanumeric match (avoiding 3year if not prepay)
  const exact = list.find((item) => {
    const itemKey = String(item.planName || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();
    if (!isPrepay && itemKey.includes("3year")) return false;
    return itemKey === clean;
  });
  if (exact) return exact;

  // 3. Normalized standard name matches
  if (clean === "free") {
    const free = list.find(
      (item) => String(item.planName || "").trim().toLowerCase() === "free"
    );
    if (free) return free;
  }
  if (clean === "trial") {
    const trial = list.find(
      (item) => String(item.planName || "").trim().toLowerCase() === "trial"
    );
    if (trial) return trial;
  }
  if (clean === "small") {
    const small = list.find(
      (item) => String(item.planName || "").trim().toLowerCase() === "small"
    );
    if (small) return small;
  }
  if (clean === "medium") {
    const medium = list.find(
      (item) => String(item.planName || "").trim().toLowerCase() === "medium"
    );
    if (medium) return medium;
  }
  if (clean === "largemedium" || clean === "largemediumpackage") {
    const lm = list.find((item) => {
      const k = String(item.planName || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      return k === "largemedium";
    });
    if (lm) return lm;
  }
  if (clean === "large") {
    const large = list.find(
      (item) => String(item.planName || "").trim().toLowerCase() === "large"
    );
    if (large) return large;
  }

  // 4. Loose fallback
  const fallback = list.find((item) => {
    const itemKey = String(item.planName || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();
    if (!isPrepay && itemKey.includes("3year")) return false;
    return itemKey.startsWith(clean) || clean.startsWith(itemKey);
  });

  return fallback || null;
}
